import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { resolveImageUrl } from '../utils/imageUtils';

export const ImageGallery = ({ images = [], alt = 'Card image' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  // Use centralized image URL resolver
  const imageList = images.length > 0 ? images.map(resolveImageUrl).filter(Boolean) : [];
  const hasMultiple = imageList.length > 1;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageList.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < imageList.length - 1 ? prev + 1 : 0));
  };

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && hasMultiple) {
      goToNext();
    }
    if (isRightSwipe && hasMultiple) {
      goToPrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageList.length]);

  if (imageList.length === 0) {
    return (
      <div className="relative aspect-[3/4] bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl flex items-center justify-center border border-white/5">
        <img 
          src={`https://placehold.co/400x533/1a1a1a/666?text=No+Image`}
          alt={alt}
          className="w-full h-full object-contain"
          style={{ zIndex: 10 }}
        />
      </div>
    );
  }

  return (
    <>
      <div 
        ref={containerRef}
        className="relative aspect-[3/4] bg-[#18181B] rounded-2xl overflow-hidden border border-white/5 group"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Main Image */}
        <img
          src={imageList[currentIndex]}
          alt={`${alt} - Image ${currentIndex + 1}`}
          className="w-full h-full transition-transform duration-300"
          style={{ objectFit: 'contain', zIndex: 10 }}
          data-testid="gallery-main-image"
        />

        {/* Fullscreen Button */}
        <button
          onClick={() => setFullscreenOpen(true)}
          className="absolute top-4 right-4 p-2 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
          data-testid="fullscreen-button"
        >
          <ZoomIn className="w-5 h-5 text-white" />
        </button>

        {/* Navigation Arrows */}
        {hasMultiple && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-3 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-black/80 hover:scale-110"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-3 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-black/80 hover:scale-110"
              data-testid="gallery-next"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {hasMultiple && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/70 rounded-full backdrop-blur-sm">
            <span className="text-sm font-medium text-white">
              {currentIndex + 1} / {imageList.length}
            </span>
          </div>
        )}

        {/* Dot Indicators */}
        {hasMultiple && (
          <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-2">
            {imageList.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white w-6' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                data-testid={`gallery-dot-${index}`}
              />
            ))}
          </div>
        )}

        {/* Swipe Hint (mobile only) */}
        {hasMultiple && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 text-xs text-white/50 sm:hidden">
            Swipe to see more
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {hasMultiple && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-zinc-700">
          {imageList.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-blue-500 ring-2 ring-blue-500/30' 
                  : 'border-white/10 hover:border-white/30'
              }`}
              data-testid={`gallery-thumbnail-${index}`}
            >
              <img 
                src={img} 
                alt={`Thumbnail ${index + 1}`} 
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Modal */}
      <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div 
            className="relative w-full h-full flex items-center justify-center"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={imageList[currentIndex]}
              alt={`${alt} - Fullscreen`}
              className="max-w-full max-h-[90vh] object-contain"
            />
            
            {hasMultiple && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-8 h-8 text-white" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </button>
              </>
            )}

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/70 rounded-full">
              <span className="text-white">{currentIndex + 1} / {imageList.length}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;
